/**
 * Livret scolaire generator — BTS NDRC (Sprint 4)
 *
 * Télécharge le template depuis Storage (bucket `livret-templates`), remplit :
 *   - Feuille "1. Informations administratives" : entête (BTS, voie, session, établissement, RNE)
 *     + lignes candidats (nom, prénom, DDN, lieu, INE, LV1)
 *   - Feuille "2. Saisie des notes" : avis conseil de classe, observations, PIX,
 *     moyennes trimestrielles par épreuve (E1–E6 NDRC), appréciations.
 *
 * Les feuilles candidats C01–C40 se remplissent toutes seules via les formules INDEX/MATCH
 * du template — on n'a pas besoin d'y toucher.
 */

import ExcelJS from 'exceljs';
import { supabase } from './supabase';

// ============================================================================
// Types
// ============================================================================

export type AppreciationPeriod = 'Y1T1' | 'Y1T2' | 'Y1T3' | 'Y2T1' | 'Y2T2' | 'Y2T3';

export type AvisConseil = 'tres_favorable' | 'favorable' | 'reserve' | 'defavorable';

export type LivretError =
  | { code: 'student_not_found' }
  | { code: 'missing_numero_candidat' }
  | { code: 'missing_session_examen' }
  | { code: 'config_not_found'; bts: string; session: number }
  | { code: 'template_download_failed'; reason: string }
  | { code: 'template_parse_failed'; reason: string };

export class LivretGenerationError extends Error {
  constructor(public readonly detail: LivretError) {
    super(JSON.stringify(detail));
    this.name = 'LivretGenerationError';
  }
}

export interface Student {
  id: string;
  first_name: string;
  last_name: string;
  class_id: string | null;
  date_naissance: string | null;
  lieu_naissance: string | null;
  ine: string | null;
  langue_vivante_1: string | null;
  sexe: 'M' | 'F' | 'X' | null;
  numero_candidat: string | null;
  session_examen: number | null;
}

export interface SessionPrecedente {
  annee: number;
  presentes: number;
  recus: number;
}

export interface LivretConfig {
  bts_code: string;
  bts_specialite: string;
  session_examen: number;
  etablissement_nom: string;
  rne: string | null;
  voie_formation: 'apprentissage' | 'initial' | 'formation_continue';
  template_storage_path: string;
  cachet_storage_path: string | null;
  sessions_precedentes: SessionPrecedente[];
}

export interface Grade {
  subject_id: string;
  grade: number | null;
  coefficient: number | null;
  exam_date: string | null;
  is_absent: boolean | null;
}

export interface AppreciationRow {
  subject_id: string | null;
  content: string;
  period: AppreciationPeriod | null;
}

export interface ConseilRow {
  avis: AvisConseil | null;
  observations: string | null;
  pix_certifie: boolean;
}

// ============================================================================
// NDRC épreuve mapping
// ============================================================================

/**
 * Mapping subject_id → colonne de départ dans la feuille "2. Saisie des notes".
 * Les blocs font 9 colonnes : Y1T1 / Y1T2 / Y1T3 / Moy Y1 / Y2T1 / Y2T2 / Y2T3 / Moy Y2 / Appréciation.
 * Les colonnes "Moy Y1" et "Moy Y2" portent une formule AVERAGE du template — on ne les écrase pas.
 */
const NDRC_SUBJECT_TO_EPREUVE: Record<string, string> = {
  '6dc63e69-ff06-46a6-b29a-5de059975399': 'E1', // Culture générale et expression
  'eea64ab6-4952-46ad-bc4f-32f641435747': 'E2', // LV1 Anglais
  'b6d9290b-5bb3-4a87-9814-5b714eab15d3': 'E3', // Culture économique, juridique et managériale
  '941bbb39-40b3-4f74-a81f-cc968a528bb0': 'E4', // Relation client et négociation-vente
  '4912d33e-f28c-4ab6-9438-d3d7ffd6f259': 'E5', // Relation client à distance et digitalisation
  'a7b8cac9-cd3b-4a6b-9ccc-8fe51acaff31': 'E6', // Relation client et animation de réseaux
};

/**
 * Colonne de départ dans la feuille "2. Saisie des notes" pour chaque épreuve.
 * Cf. template : G=E1, P=E2, Y=E3, AH=E4, AQ=E5, AZ=E6, BI=E7, BR=E8, CA=E9, CJ=E10, CS=E11, DB=E12, DK=E13, DT=E14.
 */
const EPREUVE_START_COL: Record<string, string> = {
  E1: 'G',
  E2: 'P',
  E3: 'Y',
  E4: 'AH',
  E5: 'AQ',
  E6: 'AZ',
  E7: 'BI',
  E8: 'BR',
  E9: 'CA',
  E10: 'CJ',
  E11: 'CS',
  E12: 'DB',
  E13: 'DK',
  E14: 'DT',
};

/** Offsets dans le bloc 9-colonnes pour chaque période (la moyenne est calculée par le template). */
const PERIOD_OFFSET: Record<AppreciationPeriod, number> = {
  Y1T1: 0,
  Y1T2: 1,
  Y1T3: 2,
  // offset 3 = Moy Y1 (formula)
  Y2T1: 4,
  Y2T2: 5,
  Y2T3: 6,
  // offset 7 = Moy Y2 (formula)
  // offset 8 = Appréciation
};

const APPRECIATION_OFFSET = 8;

export const AVIS_LABEL: Record<AvisConseil, string> = {
  tres_favorable: 'Très Favorable',
  favorable: 'Favorable',
  reserve: 'Réservé',
  defavorable: 'Défavorable',
};

export const VOIE_LABEL: Record<LivretConfig['voie_formation'], string> = {
  apprentissage: 'Apprentissage',
  initial: 'Scolaire',
  formation_continue: 'Formation continue',
};

// ============================================================================
// Helpers
// ============================================================================

function colLetterToIndex(col: string): number {
  let n = 0;
  for (let i = 0; i < col.length; i++) {
    n = n * 26 + (col.charCodeAt(i) - 64);
  }
  return n;
}

function numToColLetter(n: number): string {
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/**
 * Détermine la période (Y1T1..Y2T3) d'une date d'examen en fonction de la session d'examen.
 * Convention : année scolaire = septembre → août. Une note avant septembre session-2 est
 * affectée à Y1T1 (cas des rattrapages/migrations). Au-delà de la session, → Y2T3.
 */
function periodFromDate(examDate: string, sessionExamen: number): AppreciationPeriod {
  const d = new Date(examDate);
  const y = d.getFullYear();
  const m = d.getMonth() + 1; // 1..12

  const y1Start = sessionExamen - 2;
  const y2Start = sessionExamen - 1;

  // Année 1 : septembre (y1Start) → août (y2Start)
  if (y < y1Start || (y === y1Start && m < 9)) return 'Y1T1';
  if (y === y1Start) return 'Y1T1'; // sept-déc Y1
  if (y === y2Start && m <= 3) return 'Y1T2';
  if (y === y2Start && m <= 8) return 'Y1T3';
  // Année 2 : septembre (y2Start) → août (sessionExamen)
  if (y === y2Start) return 'Y2T1'; // sept-déc Y2
  if (y === sessionExamen && m <= 3) return 'Y2T2';
  if (y === sessionExamen && m <= 8) return 'Y2T3';
  return 'Y2T3';
}

/** Moyenne pondérée, arrondie à 2 décimales. Ignore les absences et notes null. */
function weightedAverage(entries: Grade[]): number | null {
  let sum = 0;
  let totalCoef = 0;
  for (const g of entries) {
    if (g.is_absent || g.grade === null) continue;
    const coef = g.coefficient ?? 1;
    sum += Number(g.grade) * coef;
    totalCoef += coef;
  }
  if (totalCoef === 0) return null;
  return Math.round((sum / totalCoef) * 100) / 100;
}

function parseCandidatRowSheet1(numeroCandidat: string): number {
  // C01 → row 8, C02 → row 9, ..., C40 → row 47
  const n = parseInt(numeroCandidat.replace(/^C0?/, ''), 10);
  return 7 + n;
}

function parseCandidatRowSheet2(numeroCandidat: string): number {
  // C01 → row 6, C02 → row 7, ..., C40 → row 45
  const n = parseInt(numeroCandidat.replace(/^C0?/, ''), 10);
  return 5 + n;
}

export function sanitizeFilename(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function livretFilename(student: Pick<Student, 'first_name' | 'last_name'>, btsCode: string, session: number): string {
  return `livret_${sanitizeFilename(student.first_name)}_${sanitizeFilename(student.last_name)}_${btsCode}_${session}.xlsx`;
}

// ============================================================================
// Cache du template (evite de re-télécharger à chaque livret dans un batch)
// ============================================================================

let cachedTemplate: { path: string; buffer: ArrayBuffer; fetchedAt: number } | null = null;
const TEMPLATE_TTL_MS = 15 * 60 * 1000; // 15 min

async function fetchTemplate(storagePath: string): Promise<ArrayBuffer> {
  if (
    cachedTemplate &&
    cachedTemplate.path === storagePath &&
    Date.now() - cachedTemplate.fetchedAt < TEMPLATE_TTL_MS
  ) {
    return cachedTemplate.buffer;
  }
  const { data, error } = await supabase.storage.from('livret-templates').download(storagePath);
  if (error || !data) {
    throw new LivretGenerationError({
      code: 'template_download_failed',
      reason: error?.message || 'no data returned',
    });
  }
  const buf = await data.arrayBuffer();
  cachedTemplate = { path: storagePath, buffer: buf, fetchedAt: Date.now() };
  return buf;
}

export function clearTemplateCache(): void {
  cachedTemplate = null;
}

// ============================================================================
// Data fetching
// ============================================================================

export async function fetchStudent(studentId: string): Promise<Student> {
  const { data, error } = await supabase
    .from('students')
    .select('id, first_name, last_name, class_id, date_naissance, lieu_naissance, ine, langue_vivante_1, sexe, numero_candidat, session_examen')
    .eq('id', studentId)
    .single();
  if (error || !data) throw new LivretGenerationError({ code: 'student_not_found' });
  return data as Student;
}

export async function fetchLivretConfig(btsCode: string, session: number): Promise<LivretConfig> {
  const { data, error } = await supabase
    .from('livret_config')
    .select(
      'bts_code, bts_specialite, session_examen, etablissement_nom, rne, voie_formation, template_storage_path, cachet_storage_path, sessions_precedentes'
    )
    .eq('bts_code', btsCode)
    .eq('session_examen', session)
    .maybeSingle();
  if (error || !data) throw new LivretGenerationError({ code: 'config_not_found', bts: btsCode, session });
  // Normalise sessions_precedentes : jsonb peut revenir null/undefined selon le driver
  const raw = data as Record<string, unknown>;
  const sp = Array.isArray(raw.sessions_precedentes)
    ? (raw.sessions_precedentes as SessionPrecedente[])
    : [];
  return { ...(data as LivretConfig), sessions_precedentes: sp };
}

export async function fetchGrades(studentId: string): Promise<Grade[]> {
  const { data, error } = await supabase
    .from('grades')
    .select('subject_id, grade, coefficient, exam_date, is_absent')
    .eq('student_id', studentId);
  if (error) throw error;
  return (data ?? []) as Grade[];
}

export async function fetchAppreciations(studentId: string): Promise<AppreciationRow[]> {
  const { data, error } = await supabase
    .from('appreciations')
    .select('subject_id, content, period')
    .eq('student_id', studentId);
  if (error) throw error;
  return (data ?? []) as AppreciationRow[];
}

export async function fetchConseil(studentId: string, session: number): Promise<ConseilRow | null> {
  const { data, error } = await supabase
    .from('livret_conseil_classe')
    .select('avis, observations, pix_certifie')
    .eq('student_id', studentId)
    .eq('session_year', session)
    .maybeSingle();
  if (error) throw error;
  return (data as ConseilRow | null) ?? null;
}

// ============================================================================
// Aggregation
// ============================================================================

/** Moyenne des notes par (subject, period). */
export function aggregateGrades(
  grades: Grade[],
  sessionExamen: number
): Map<string /* subjectId */, Map<AppreciationPeriod, number>> {
  const bySubject = new Map<string, Map<AppreciationPeriod, Grade[]>>();
  for (const g of grades) {
    if (!g.exam_date) continue;
    const period = periodFromDate(g.exam_date, sessionExamen);
    if (!bySubject.has(g.subject_id)) bySubject.set(g.subject_id, new Map());
    const byPeriod = bySubject.get(g.subject_id)!;
    if (!byPeriod.has(period)) byPeriod.set(period, []);
    byPeriod.get(period)!.push(g);
  }
  const out = new Map<string, Map<AppreciationPeriod, number>>();
  for (const [subj, byPeriod] of bySubject) {
    const m = new Map<AppreciationPeriod, number>();
    for (const [period, arr] of byPeriod) {
      const avg = weightedAverage(arr);
      if (avg !== null) m.set(period, avg);
    }
    out.set(subj, m);
  }
  return out;
}

/** Concatène les appréciations par (subject) — on privilégie la période Y2T3 si disponible, sinon on concat. */
export function aggregateAppreciations(
  apprs: AppreciationRow[]
): Map<string /* subjectId */, string> {
  const bySubject = new Map<string, AppreciationRow[]>();
  for (const a of apprs) {
    if (!a.subject_id) continue;
    if (!bySubject.has(a.subject_id)) bySubject.set(a.subject_id, []);
    bySubject.get(a.subject_id)!.push(a);
  }
  const out = new Map<string, string>();
  for (const [subj, arr] of bySubject) {
    // Priorité : Y2T3 > Y2T2 > Y2T1 > Y1T3 > Y1T2 > Y1T1 > null
    const order: (AppreciationPeriod | null)[] = ['Y2T3', 'Y2T2', 'Y2T1', 'Y1T3', 'Y1T2', 'Y1T1', null];
    let chosen: string | null = null;
    for (const p of order) {
      const match = arr.find((a) => a.period === p);
      if (match && match.content && match.content.trim() !== '') {
        chosen = match.content.trim();
        break;
      }
    }
    if (chosen) out.set(subj, chosen);
  }
  return out;
}

// ============================================================================
// Sheet filling
// ============================================================================

function fillSheet1Header(ws: ExcelJS.Worksheet, config: LivretConfig): void {
  ws.getCell('C3').value = 'BTS';
  ws.getCell('C4').value = config.bts_specialite;
  ws.getCell('L3').value = VOIE_LABEL[config.voie_formation];
  ws.getCell('L4').value = config.session_examen;
  ws.getCell('L5').value = config.etablissement_nom;
  if (config.rne) ws.getCell('L6').value = config.rne;
}

/**
 * Efface toutes les cellules de candidats (C01–C40) dans les deux feuilles AVANT de remplir
 * avec les vraies données. Nécessaire parce que le template peut contenir des noms de test
 * résiduels, ou parce qu'un livret mono-candidat doit n'afficher QUE le candidat concerné.
 *
 * IMPORTANT : on NE touche PAS aux formules (colonnes Moy Y1 offset 3, Moy Y2 offset 7).
 */
function clearCandidateRows(sheet1: ExcelJS.Worksheet, sheet2: ExcelJS.Worksheet): void {
  // Sheet 1 : rows 8..47, colonnes A..H (numero, nom, prénom, DDN, lieu, [F réservée], INE, LV1)
  const SHEET1_COLS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  for (let row = 8; row <= 47; row++) {
    for (const col of SHEET1_COLS) {
      sheet1.getCell(`${col}${row}`).value = null;
    }
  }

  // Sheet 2 : rows 6..45, colonnes D (avis), E (observations), F (PIX)
  for (let row = 6; row <= 45; row++) {
    sheet2.getCell(`D${row}`).value = null;
    sheet2.getCell(`E${row}`).value = null;
    sheet2.getCell(`F${row}`).value = null;

    // Pour chaque épreuve, wipe les offsets data (0,1,2,4,5,6,8) mais skip formules (3, 7)
    for (const startCol of Object.values(EPREUVE_START_COL)) {
      const startIdx = colLetterToIndex(startCol);
      const dataOffsets = [0, 1, 2, 4, 5, 6, APPRECIATION_OFFSET];
      for (const off of dataOffsets) {
        const col = numToColLetter(startIdx + off);
        sheet2.getCell(`${col}${row}`).value = null;
      }
    }
  }
}

function fillSheet1Candidate(ws: ExcelJS.Worksheet, student: Student): void {
  if (!student.numero_candidat) return;
  const row = parseCandidatRowSheet1(student.numero_candidat);
  ws.getCell(`A${row}`).value = student.numero_candidat;
  ws.getCell(`B${row}`).value = student.last_name.toUpperCase();
  ws.getCell(`C${row}`).value = student.first_name;
  if (student.date_naissance) {
    // ExcelJS accepte Date native
    ws.getCell(`D${row}`).value = new Date(student.date_naissance);
    ws.getCell(`D${row}`).numFmt = 'dd/mm/yyyy';
  }
  if (student.lieu_naissance) ws.getCell(`E${row}`).value = student.lieu_naissance;
  if (student.ine) ws.getCell(`G${row}`).value = student.ine;
  if (student.langue_vivante_1) ws.getCell(`H${row}`).value = student.langue_vivante_1;
}

function fillSheet2Candidate(
  ws: ExcelJS.Worksheet,
  student: Student,
  grades: Map<string, Map<AppreciationPeriod, number>>,
  apprsBySubject: Map<string, string>,
  conseil: ConseilRow | null
): void {
  if (!student.numero_candidat) return;
  const row = parseCandidatRowSheet2(student.numero_candidat);

  // Avis / Observations / PIX
  if (conseil?.avis) ws.getCell(`D${row}`).value = AVIS_LABEL[conseil.avis];
  if (conseil?.observations) ws.getCell(`E${row}`).value = conseil.observations;
  ws.getCell(`F${row}`).value = conseil?.pix_certifie ? 'OUI' : 'NON';

  // Notes par épreuve / période
  for (const [subjectId, epreuve] of Object.entries(NDRC_SUBJECT_TO_EPREUVE)) {
    const startCol = EPREUVE_START_COL[epreuve];
    if (!startCol) continue;
    const byPeriod = grades.get(subjectId);
    if (byPeriod) {
      for (const [period, offset] of Object.entries(PERIOD_OFFSET)) {
        const val = byPeriod.get(period as AppreciationPeriod);
        if (val !== undefined) {
          const colLetter = numToColLetter(colLetterToIndex(startCol) + offset);
          ws.getCell(`${colLetter}${row}`).value = val;
        }
      }
    }
    // Appréciation (offset 8)
    const apprText = apprsBySubject.get(subjectId);
    if (apprText) {
      const appColLetter = numToColLetter(colLetterToIndex(startCol) + APPRECIATION_OFFSET);
      ws.getCell(`${appColLetter}${row}`).value = apprText;
    }
  }
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Génère le livret scolaire d'un étudiant et retourne un Blob xlsx.
 * @throws LivretGenerationError si données manquantes (numero_candidat, session, config, template).
 */
export async function generateLivret(studentId: string): Promise<{ blob: Blob; filename: string }> {
  const student = await fetchStudent(studentId);
  if (!student.numero_candidat) throw new LivretGenerationError({ code: 'missing_numero_candidat' });
  if (!student.session_examen) throw new LivretGenerationError({ code: 'missing_session_examen' });

  // Pour l'instant, le BTS code est hardcodé NDRC (la classe détermine ça).
  // Futur : ajouter `bts_code` sur `classes` et le lire ici.
  const btsCode = 'NDRC';

  const [config, grades, apprs, conseil] = await Promise.all([
    fetchLivretConfig(btsCode, student.session_examen),
    fetchGrades(studentId),
    fetchAppreciations(studentId),
    fetchConseil(studentId, student.session_examen),
  ]);

  const templateBuffer = await fetchTemplate(config.template_storage_path);

  const workbook = new ExcelJS.Workbook();
  try {
    await workbook.xlsx.load(templateBuffer);
  } catch (e) {
    throw new LivretGenerationError({
      code: 'template_parse_failed',
      reason: e instanceof Error ? e.message : String(e),
    });
  }

  const sheet1 = workbook.getWorksheet('1. Informations administratives');
  const sheet2 = workbook.getWorksheet('2. Saisie des notes');
  if (!sheet1 || !sheet2) {
    throw new LivretGenerationError({
      code: 'template_parse_failed',
      reason: 'Feuilles "1. Informations administratives" ou "2. Saisie des notes" introuvables dans le template.',
    });
  }

  // IMPORTANT : on force recalcul à l'ouverture pour que les moyennes Y1/Y2 et les feuilles candidats
  // (qui utilisent CELL("nomfichier") + INDEX/MATCH) se recalculent chez l'utilisateur final.
  const calcProps = (workbook as unknown as { calcProperties?: { fullCalcOnLoad?: boolean } }).calcProperties;
  if (calcProps) calcProps.fullCalcOnLoad = true;

  // Wipe défensif : le template peut contenir des candidats de test, et un livret mono-étudiant
  // ne doit montrer QUE ce candidat (pas les 39 autres rows).
  clearCandidateRows(sheet1, sheet2);

  fillSheet1Header(sheet1, config);
  fillSheet1Candidate(sheet1, student);

  const aggregatedGrades = aggregateGrades(grades, student.session_examen);
  const apprsBySubject = aggregateAppreciations(apprs);
  fillSheet2Candidate(sheet2, student, aggregatedGrades, apprsBySubject, conseil);

  const out = await workbook.xlsx.writeBuffer();
  const blob = new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  return {
    blob,
    filename: livretFilename(student, btsCode, student.session_examen),
  };
}

/**
 * Génère un livret consolidé pour toute une classe — remplit tous les candidats dans un seul fichier.
 * Utilisé pour le batch admin (Sprint 6).
 */
export async function generateLivretClasse(classId: string): Promise<{ blob: Blob; filename: string }> {
  const { data: students, error } = await supabase
    .from('students')
    .select('id, first_name, last_name, class_id, date_naissance, lieu_naissance, ine, langue_vivante_1, sexe, numero_candidat, session_examen')
    .eq('class_id', classId)
    .not('numero_candidat', 'is', null)
    .order('numero_candidat', { ascending: true });
  if (error || !students || students.length === 0) {
    throw new LivretGenerationError({ code: 'student_not_found' });
  }

  const firstStudent = students[0] as Student;
  const session = firstStudent.session_examen;
  if (!session) throw new LivretGenerationError({ code: 'missing_session_examen' });

  const btsCode = 'NDRC';
  const config = await fetchLivretConfig(btsCode, session);
  const templateBuffer = await fetchTemplate(config.template_storage_path);

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(templateBuffer);

  const sheet1 = workbook.getWorksheet('1. Informations administratives');
  const sheet2 = workbook.getWorksheet('2. Saisie des notes');
  if (!sheet1 || !sheet2) {
    throw new LivretGenerationError({
      code: 'template_parse_failed',
      reason: 'Feuilles introuvables',
    });
  }
  // @ts-expect-error - calcProperties présent à l'exécution
  if (workbook.calcProperties) workbook.calcProperties.fullCalcOnLoad = true;

  // Wipe défensif : le template peut contenir des candidats de test résiduels.
  clearCandidateRows(sheet1, sheet2);

  fillSheet1Header(sheet1, config);

  for (const s of students) {
    const st = s as Student;
    const [grades, apprs, conseil] = await Promise.all([
      fetchGrades(st.id),
      fetchAppreciations(st.id),
      fetchConseil(st.id, session),
    ]);
    fillSheet1Candidate(sheet1, st);
    fillSheet2Candidate(
      sheet2,
      st,
      aggregateGrades(grades, session),
      aggregateAppreciations(apprs),
      conseil
    );
  }

  const out = await workbook.xlsx.writeBuffer();
  return {
    blob: new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename: `livret_classe_${btsCode}_${session}.xlsx`,
  };
}

// ============================================================================
// Completeness check (pour l'admin batch Sprint 6)
// ============================================================================

export interface CompletenessReport {
  student_id: string;
  first_name: string;
  last_name: string;
  numero_candidat: string | null;
  status: 'complete' | 'partial' | 'incomplete';
  missing: string[];
}

export async function checkCompleteness(classId: string, session: number): Promise<CompletenessReport[]> {
  const { data: students, error } = await supabase
    .from('students')
    .select('id, first_name, last_name, numero_candidat, date_naissance, lieu_naissance, ine, langue_vivante_1, session_examen')
    .eq('class_id', classId);
  if (error || !students) return [];

  const { data: conseilRows } = await supabase
    .from('livret_conseil_classe')
    .select('student_id, avis')
    .eq('session_year', session);
  const conseilByStudent = new Map<string, string | null>();
  for (const r of conseilRows ?? []) conseilByStudent.set(r.student_id, r.avis);

  const reports: CompletenessReport[] = [];
  for (const s of students) {
    const missing: string[] = [];
    if (!s.numero_candidat) missing.push('numero_candidat');
    if (!s.date_naissance) missing.push('date_naissance');
    if (!s.lieu_naissance) missing.push('lieu_naissance');
    if (!s.ine) missing.push('INE');
    if (!s.langue_vivante_1) missing.push('LV1');
    if (!s.session_examen) missing.push('session');
    if (!conseilByStudent.has(s.id)) missing.push('avis_conseil_classe');
    const status: CompletenessReport['status'] =
      missing.length === 0 ? 'complete' : missing.length <= 2 ? 'partial' : 'incomplete';
    reports.push({
      student_id: s.id,
      first_name: s.first_name,
      last_name: s.last_name,
      numero_candidat: s.numero_candidat,
      status,
      missing,
    });
  }
  return reports.sort((a, b) => (a.numero_candidat ?? 'ZZ').localeCompare(b.numero_candidat ?? 'ZZ'));
}
